import { Link } from "react-router-dom";
import {
  ArrowLeft, Flame, Beef, Wheat, Droplet, Sunrise, Sun, Sunset, Moon,
  Apple, Coffee, Dumbbell, Pill, Check, ChevronRight,
} from "lucide-react";
import { TARGETS, MEALS, TOTAL, SUPPLEMENTS } from "./nutritionData";
import { TRAINEE } from "./data";

const ICONS = { Flame, Beef, Wheat, Droplet, Sunrise, Sun, Sunset, Moon, Apple, Coffee, Dumbbell };

export default function Nutrition() {
  return (
    <div className="min-h-screen bg-background text-foreground" data-testid="nutrition-page">
      {/* TOP NAV */}
      <header className="sticky top-0 z-40 bg-background/90 backdrop-blur-xl border-b border-border">
        <div className="container-x flex items-center justify-between h-14">
          <Link to="/goodpt" className="inline-flex items-center gap-2 text-muted-foreground hover:text-primary text-sm font-display uppercase tracking-wider" data-testid="nutrition-back">
            <ArrowLeft className="h-4 w-4" /> Antrenmana Dön
          </Link>
          <div className="flex items-center gap-2">
            <img src="/assets/fcs-logo.png" alt="FCS" className="h-9 w-9" />
            <span className="font-display font-black uppercase tracking-wider text-sm">GoodPT · Beslenme</span>
          </div>
          <div className="w-12" />
        </div>
      </header>

      {/* HERO */}
      <section className="relative overflow-hidden border-b border-border">
        <div className="absolute inset-0 grid-bg opacity-50" />
        <div className="absolute -top-24 -right-24 h-[320px] w-[320px] rounded-full bg-emerald-500/15 blur-3xl" />
        <div className="absolute -bottom-24 -left-24 h-[320px] w-[320px] rounded-full bg-primary/15 blur-3xl" />

        <div className="container-x relative py-8 md:py-14 text-center">
          <div className="inline-flex items-center gap-2 px-3 py-1.5 rounded-full bg-emerald-500/15 ring-1 ring-emerald-500/30 mb-4">
            <span className="h-1.5 w-1.5 rounded-full bg-emerald-400 pulse-soft" />
            <span className="text-[10px] font-display font-bold uppercase tracking-[0.25em] text-emerald-300">Kişisel Beslenme Planı</span>
          </div>
          <div className="text-xs font-display uppercase tracking-[0.3em] text-muted-foreground mb-3">{TRAINEE.name} için</div>
          <h1 className="font-brand uppercase leading-[0.9]" style={{ fontSize: "clamp(2.25rem, 8vw, 4.5rem)" }}>
            Beslenme & Supplement
          </h1>
          <p className="mt-3 text-sm md:text-base text-muted-foreground max-w-md mx-auto leading-relaxed">
            V-Shape hedefine uyumlu, 5 öğünlük günlük plan ve destek programı.
          </p>
        </div>
      </section>

      {/* DAILY TARGETS */}
      <section className="py-6 border-b border-border bg-[hsl(218_55%_5%)]">
        <div className="container-x">
          <div className="text-center mb-5">
            <div className="eyebrow justify-center mb-1">Günlük Hedef</div>
          </div>
          <div className="grid grid-cols-2 sm:grid-cols-4 gap-3" data-testid="daily-targets">
            {TARGETS.map((t) => {
              const Icon = ICONS[t.icon] || Flame;
              return (
                <div key={t.label} className="card-elev p-4 text-center">
                  <div className="h-9 w-9 rounded-md bg-emerald-500/15 ring-1 ring-emerald-500/30 flex items-center justify-center text-emerald-300 mx-auto mb-2">
                    <Icon className="h-4 w-4" />
                  </div>
                  <div className="font-display text-2xl font-black text-foreground leading-none">
                    {t.value}
                    <span className="text-xs text-muted-foreground ml-1">{t.unit}</span>
                  </div>
                  <div className="mt-1 text-[10px] font-display uppercase tracking-[0.2em] text-muted-foreground">{t.label}</div>
                </div>
              );
            })}
          </div>
        </div>
      </section>

      {/* MEALS */}
      <section className="section-pad">
        <div className="container-x">
          <div className="text-center mb-8">
            <div className="eyebrow justify-center mb-2">Günlük Öğün Planı</div>
            <h2 className="heading-lg">5 Öğün Düzeni</h2>
          </div>

          <div className="space-y-4 max-w-3xl mx-auto" data-testid="meal-list">
            {MEALS.map((meal, i) => {
              const Icon = ICONS[meal.icon] || Sun;
              return (
                <article key={meal.key} className="card-elev p-5 md:p-6" data-testid={`meal-${meal.key}`}>
                  <header className="flex items-start justify-between gap-4 mb-4 pb-4 border-b border-border">
                    <div className="flex items-center gap-3">
                      <div className="h-11 w-11 rounded-md bg-primary/10 ring-1 ring-primary/30 flex items-center justify-center text-primary shrink-0">
                        <Icon className="h-5 w-5" />
                      </div>
                      <div>
                        <div className="text-[10px] font-display font-bold uppercase tracking-[0.22em] text-muted-foreground">{String(i + 1).padStart(2, "0")} · {meal.time}</div>
                        <div className="font-display text-xl font-black uppercase tracking-tight">{meal.title}</div>
                      </div>
                    </div>
                  </header>

                  <ul className="space-y-2 mb-4">
                    {meal.items.map((item, idx) => (
                      <li key={idx} className="flex items-start gap-2.5 text-sm">
                        <Check className="h-4 w-4 text-emerald-400 mt-0.5 shrink-0" />
                        <span>{item}</span>
                      </li>
                    ))}
                  </ul>

                  {meal.alternatives && (
                    <AltBlock label="Alternatifler" items={meal.alternatives} />
                  )}
                  {meal.extraAlt && (
                    <AltBlock label={meal.extraAlt.label} items={meal.extraAlt.items} />
                  )}

                  <div className="mt-4 pt-4 border-t border-border grid grid-cols-3 gap-2">
                    <Macro label="Protein" value={meal.macros.protein} color="emerald" />
                    <Macro label="Karb." value={meal.macros.carbs} color="amber" />
                    <Macro label="Yağ" value={meal.macros.fat} color="primary" />
                  </div>
                </article>
              );
            })}
          </div>
        </div>
      </section>

      {/* DAILY TOTAL */}
      <section className="py-10 border-y border-border bg-gradient-to-br from-emerald-500/10 via-background to-background" data-testid="daily-total">
        <div className="container-x max-w-3xl mx-auto">
          <div className="text-center mb-6">
            <div className="eyebrow justify-center mb-1">Günlük Toplam</div>
            <h3 className="heading-md">Tamamladığında</h3>
          </div>
          <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
            <BigStat label="Protein" value={`${TOTAL.protein} gr`} accent="emerald" />
            <BigStat label="Karbonhidrat" value={`${TOTAL.carbs} gr`} accent="amber" />
            <BigStat label="Yağ" value={`${TOTAL.fat} gr`} accent="primary" />
            <BigStat label="Kalori" value={`${TOTAL.kcal} kcal`} accent="rose" />
          </div>
        </div>
      </section>

      {/* SUPPLEMENTS */}
      <section className="section-pad" data-testid="supplements">
        <div className="container-x">
          <div className="text-center mb-8">
            <div className="eyebrow justify-center mb-2">Destekleyici Program</div>
            <h2 className="heading-lg">Supplement Planı</h2>
            <p className="mt-3 text-sm text-muted-foreground max-w-md mx-auto">
              Belirtilen saatlerde, üzerinde yazan dozda al. Gerekirse hekimine danış.
            </p>
          </div>

          <div className="grid sm:grid-cols-2 gap-4 max-w-3xl mx-auto">
            {SUPPLEMENTS.map((s) => {
              const Icon = ICONS[s.icon] || Pill;
              return (
                <div key={s.time} className="card-elev p-5" data-testid={`supp-${s.time}`}>
                  <div className="flex items-center gap-3 mb-4 pb-3 border-b border-border">
                    <div className="h-10 w-10 rounded-md bg-primary/10 ring-1 ring-primary/30 flex items-center justify-center text-primary">
                      <Icon className="h-5 w-5" />
                    </div>
                    <div>
                      <div className="text-[10px] font-display uppercase tracking-[0.22em] text-muted-foreground">Zaman</div>
                      <div className="font-display font-bold uppercase tracking-wider">{s.time}</div>
                    </div>
                  </div>
                  <ul className="space-y-3">
                    {s.items.map((it, idx) => (
                      <li key={idx} className="flex items-center gap-3">
                        <Pill className="h-4 w-4 text-primary shrink-0" />
                        <div className="flex-1 min-w-0">
                          <div className="font-display font-bold uppercase tracking-wider text-sm truncate">{it.name}</div>
                          <div className="text-xs text-muted-foreground">{it.dose}</div>
                        </div>
                      </li>
                    ))}
                  </ul>
                </div>
              );
            })}
          </div>
        </div>
      </section>

      {/* TIPS */}
      <section className="border-t border-border bg-[hsl(218_55%_5%)] py-10">
        <div className="container-x max-w-3xl mx-auto">
          <div className="card-elev p-5 md:p-6">
            <div className="eyebrow mb-3">Hatırlatma</div>
            <ul className="space-y-2.5 text-sm text-muted-foreground">
              <li className="flex items-start gap-2"><span className="text-primary">▸</span><span>Günde 2.5-3 litre su iç.</span></li>
              <li className="flex items-start gap-2"><span className="text-primary">▸</span><span>Antrenman günleri 1 saat öncesi protein + karbonhidrat al.</span></li>
              <li className="flex items-start gap-2"><span className="text-primary">▸</span><span>Hafta sonu 1 cheat meal yapabilirsin, ama porsiyon kontrolü şart.</span></li>
              <li className="flex items-start gap-2"><span className="text-primary">▸</span><span>Beslenme planı zorlanırsa antrenörüne danış.</span></li>
            </ul>
          </div>

          <div className="mt-6 text-center">
            <Link to="/goodpt" className="btn-secondary" data-testid="nutrition-back-bottom">
              Antrenman Programına Dön <ChevronRight className="h-4 w-4" />
            </Link>
          </div>
        </div>
      </section>

      <footer className="border-t border-border py-6 text-center text-xs text-muted-foreground">
        <img src="/assets/fcs-logo.png" alt="FCS" className="h-10 w-10 mx-auto mb-2" />
        Focus Spor Kulübü · GoodPT Beslenme Planı
      </footer>
    </div>
  );
}

function AltBlock({ label, items }) {
  return (
    <div className="mb-1">
      <div className="text-[10px] font-display font-bold uppercase tracking-[0.22em] text-muted-foreground mb-2">{label}</div>
      <div className="flex flex-wrap gap-1.5">
        {items.map((alt, idx) => (
          <span key={idx} className="rounded-full border border-border bg-secondary px-2.5 py-1 text-xs font-display font-bold uppercase tracking-wider">
            {alt}
          </span>
        ))}
      </div>
    </div>
  );
}

function Macro({ label, value, color }) {
  const colorMap = {
    emerald: "text-emerald-400",
    amber: "text-amber-300",
    primary: "text-primary",
  };
  return (
    <div className="text-center rounded-md bg-background/50 border border-border py-2">
      <div className={`font-display text-lg font-black ${colorMap[color]} leading-none`}>{value}<span className="text-[10px] text-muted-foreground ml-0.5">gr</span></div>
      <div className="mt-1 text-[10px] font-display uppercase tracking-[0.18em] text-muted-foreground">{label}</div>
    </div>
  );
}

function BigStat({ label, value, accent }) {
  const map = {
    emerald: "text-emerald-400",
    amber: "text-amber-300",
    primary: "text-primary",
    rose: "text-rose-400",
  };
  return (
    <div className="card-elev p-4 text-center">
      <div className={`font-display text-2xl md:text-3xl font-black ${map[accent]} leading-none`}>{value}</div>
      <div className="mt-2 text-[10px] font-display uppercase tracking-[0.22em] text-muted-foreground">{label}</div>
    </div>
  );
}
